import { useSyncExternalStore } from 'react';
import type { InfoPageId } from '../content/infoPages.ts';
import { LANG_COOKIE, localeOfPath, localizePath, stripLocale, type SeoLocale } from '../../shared/seo.ts';

/**
 * Minimal History-API router; a dependency would add more weight than it removes.
 *
 * Every page exists twice: Turkish at the root and English under /en. Routes
 * and links are written without the prefix; the router keeps the visitor in
 * the language of the URL they are on.
 */

const listeners = new Set<() => void>();

function subscribe(listener: () => void) {
  listeners.add(listener);
  window.addEventListener('popstate', listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('popstate', listener);
  };
}

/** The current language, taken from the URL. */
export function currentLocale(): SeoLocale {
  return localeOfPath(window.location.pathname);
}

/** An app path ("/market") in the current (or given) language ("/en/market"). */
export function localizeHref(to: string, locale: SeoLocale = currentLocale()): string {
  if (!to.startsWith('/') || to.startsWith('//') || to.startsWith('/api/')) return to;
  const [path, rest = ''] = to.split(/(?=[?#])/, 2);
  return localizePath(path, locale) + rest;
}

/**
 * A page can ask to confirm before the visitor leaves it (a live online game).
 * While a guard is set, in-app navigation and the Back button call `onBlock`
 * with the destination instead of leaving; the page then confirms and calls
 * `navigate(to, { force: true })`. Switching language stays on the page and is
 * never blocked.
 */
let leaveGuard: { url: string; onBlock: (to: string) => void } | null = null;

export function setLeaveGuard(onBlock: ((to: string) => void) | null) {
  leaveGuard = onBlock ? { url: window.location.pathname + window.location.search, onBlock } : null;
}

const samePage = (a: string, b: string) => stripLocale(a.split(/[?#]/, 1)[0]) === stripLocale(b.split(/[?#]/, 1)[0]);

// Registered before any router listener, so it can stop a guarded Back press first.
if (typeof window !== 'undefined') {
  window.addEventListener('popstate', (event) => {
    const guard = leaveGuard;
    const to = window.location.pathname + window.location.search;
    if (!guard || samePage(to, guard.url)) return;
    event.stopImmediatePropagation();
    window.history.pushState(null, '', guard.url);
    guard.onBlock(to);
  });
}

export function navigate(to: string, options: { replace?: boolean; locale?: SeoLocale; force?: boolean } = {}) {
  to = localizeHref(to, options.locale);
  if (to === window.location.pathname + window.location.search) return;
  if (leaveGuard && !options.force && !samePage(to, leaveGuard.url)) {
    leaveGuard.onBlock(to);
    return;
  }
  if (options.replace) window.history.replaceState(null, '', to);
  else window.history.pushState(null, '', to);
  window.scrollTo(0, 0);
  listeners.forEach((l) => l());
}

/** The current path without its language prefix. */
export function usePathname(): string {
  return useSyncExternalStore(subscribe, () => stripLocale(window.location.pathname));
}

export function useUrlLocale(): SeoLocale {
  return useSyncExternalStore(subscribe, currentLocale);
}

/** Move to the same page in the other language and remember the choice (also for the server). */
export function switchLocale(locale: SeoLocale) {
  document.cookie = `${LANG_COOKIE}=${locale}; Path=/; Max-Age=31536000; SameSite=Lax`;
  navigate(window.location.pathname + window.location.search, { replace: true, locale });
}

export type Route =
  | { name: 'home' }
  | { name: 'computer' }
  | { name: 'computerGame' }
  | { name: 'friend' }
  | { name: 'online' }
  | { name: 'leaderboard' }
  | { name: 'market' }
  | { name: 'gifts' }
  | { name: 'me' }
  | { name: 'profile'; username: string }
  | { name: 'room'; code: string }
  | { name: 'info'; page: InfoPageId }
  | { name: 'notFound' };

export function matchRoute(pathname: string): Route {
  const path = pathname.replace(/\/+$/, '') || '/';
  if (path === '/') return { name: 'home' };
  if (path === '/computer') return { name: 'computer' };
  if (path === '/computer/play') return { name: 'computerGame' };
  if (path === '/friend') return { name: 'friend' };
  if (path === '/online') return { name: 'online' };
  if (path === '/leaderboard') return { name: 'leaderboard' };
  if (path === '/market') return { name: 'market' };
  if (path === '/gifts') return { name: 'gifts' };
  if (path === '/me') return { name: 'me' };
  if (path === '/how-to-play') return { name: 'info', page: 'guide' };
  if (path === '/privacy') return { name: 'info', page: 'privacy' };
  if (path === '/about') return { name: 'info', page: 'about' };
  const profile = /^\/u\/([A-Za-z0-9_]{1,20})$/.exec(path);
  if (profile) return { name: 'profile', username: profile[1] };
  const room = /^\/r\/([A-Za-z0-9]+)$/.exec(path);
  if (room) return { name: 'room', code: room[1].toUpperCase() };
  return { name: 'notFound' };
}
